// src/components/CriticalGapsWarning.tsx
import React from 'react';
import { CriticalGap } from '../types';
import './CriticalGapsWarning.css';

interface CriticalGapsWarningProps {
  gaps: CriticalGap[];
}

export const CriticalGapsWarning: React.FC<CriticalGapsWarningProps> = ({ gaps }) => {
  if (!gaps || gaps.length === 0) {
    return null;
  }

  const criticalGaps = gaps.filter(g => g.severity === 'CRITICAL');
  const highGaps = gaps.filter(g => g.severity === 'HIGH');

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return 'critical';
      case 'HIGH':
        return 'high';
      case 'MODERATE':
        return 'moderate';
      default:
        return 'moderate';
    }
  };

  return (
    <div className="critical-gaps-warning">
      <div className="warning-header">
        <div className="warning-icon">🚨</div>
        <div className="warning-content">
          <h2 className="warning-title">Critical Compliance Gaps Detected</h2>
          <p className="warning-subtitle">
            {criticalGaps.length > 0 && (
              <span className="critical-count">
                {criticalGaps.length} CRITICAL issue{criticalGaps.length > 1 ? 's' : ''}
              </span>
            )}
            {criticalGaps.length > 0 && highGaps.length > 0 && <span> • </span>}
            {highGaps.length > 0 && (
              <span className="high-count">
                {highGaps.length} HIGH priority issue{highGaps.length > 1 ? 's' : ''}
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="gaps-list">
        {gaps.map((gap, index) => (
          <div key={index} className={`gap-item gap-${getSeverityColor(gap.severity)}`}>
            <div className="gap-header">
              <div className="gap-title-section">
                <span className={`severity-badge severity-${getSeverityColor(gap.severity)}`}>
                  {gap.severity}
                </span>
                <h3 className="gap-title">{gap.title}</h3>
              </div>
              <div className="gap-meta">
                <span className="gap-timeframe">⏱️ {gap.timeframe}</span>
                {gap.estimatedCost && (
                  <span className="gap-cost">💰 {gap.estimatedCost}</span>
                )}
              </div>
            </div>

            <div className="gap-body">
              <div className="gap-regulation">
                <strong>Regulation:</strong> {gap.regulation}
              </div>
              <div className="gap-action">
                <strong>Required Action:</strong> {gap.action}
              </div>
              {gap.riskDescription && (
                <div className="gap-risk">
                  <strong>Risk:</strong> {gap.riskDescription}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="warning-footer">
        <p className="footer-text">
          ⚠️ These gaps require immediate attention to achieve HIPAA compliance.
        </p>
      </div>
    </div>
  );
};
